import { startAuthentication } from '@simplewebauthn/browser';
import { useEffect, useMemo, useState } from 'react';
import { begin_login, finish_login } from '../api/kernel_client';
import { CeremonyChrome } from '../components/ceremony_chrome';
import { get_translations } from '../i18n/translations';
import { probe_passkey_support } from '../lib/capability_probe';
import { read_redirect_params } from '../lib/url_params';

/**
 * /redirect/login — fallback path for mobile / popup-blocked browsers,
 * also used by Capacitor's system-browser flow.
 *
 * Difference vs popup_login:
 *   - No postMessage. On success/failure we navigate the user back to
 *     `<return_origin>/sso/callback?ticket=...&state=...` (or for
 *     Capacitor deep-link schemes: `<scheme>?ticket=...&state=...`).
 *   - read_redirect_params validates `return_origin` against an
 *     ALLOWED_RETURN_ORIGINS allow-list so the auth-origin can never be
 *     abused as an open redirect to an attacker-controlled URL.
 */
export function RedirectLoginRoute() {
  const t = useMemo(() => get_translations(), []);
  const params = useMemo(() => read_redirect_params(window.location.search), []);
  const [supported, set_supported] = useState<boolean | null>(null);
  const [state, set_state] = useState<
    'idle' | 'running' | 'success' | { kind: 'error'; message: string }
  >('idle');

  useEffect(() => {
    void probe_passkey_support().then((ok) => set_supported(ok));
  }, []);

  async function run_ceremony() {
    if (params === null) return;
    set_state('running');
    try {
      const opts = await begin_login({
        ...(params.username !== undefined ? { username: params.username } : {}),
        api_client_id: params.api_client_id,
        return_origin: params.return_origin,
      });
      const credential = await startAuthentication({
        optionsJSON: opts.public_key as unknown as Parameters<
          typeof startAuthentication
        >[0]['optionsJSON'],
      });
      const result = await finish_login({ challenge_id: opts.challenge_id, credential });
      set_state('success');
      const target = build_callback_url(params.return_origin, {
        ticket: result.sso_ticket,
        state: params.state,
      });
      window.location.replace(target);
    } catch (e) {
      const message = e instanceof Error ? e.message : t.error_unknown;
      const target = build_callback_url(params.return_origin, {
        error: message,
        state: params.state,
      });
      set_state({ kind: 'error', message });
      window.setTimeout(() => window.location.replace(target), 1500);
    }
  }

  if (params === null) {
    return <CeremonyChrome title={t.app_title} body={<p>Invalid launch parameters.</p>} />;
  }
  if (supported === false) {
    return <CeremonyChrome title={t.app_title} body={<p>{t.error_no_support}</p>} />;
  }
  if (supported === null) {
    return <CeremonyChrome title={t.app_title} body={<p>{t.waiting}</p>} />;
  }

  return (
    <CeremonyChrome
      title={t.app_title}
      body={
        <>
          {state === 'idle' || (typeof state === 'object' && state.kind === 'error') ? (
            <button onClick={run_ceremony} style={primary_button_style}>
              {t.use_passkey}
            </button>
          ) : null}
          {state === 'running' ? <p style={{ color: '#545454' }}>{t.waiting}</p> : null}
          {state === 'success' ? <p style={{ color: '#0a7c1f' }}>{t.success}</p> : null}
          {typeof state === 'object' && state.kind === 'error' ? (
            <p role="alert" style={{ color: '#b00020' }}>
              {state.message}
            </p>
          ) : null}
        </>
      }
    />
  );
}

function build_callback_url(return_origin: string, params: Record<string, string>): string {
  // For HTTPS origins we redirect to <return_origin>/sso/callback?...; for
  // deep-link schemes the scheme already encodes the callback path, so we
  // append the params directly.
  const is_https = return_origin.startsWith('https://');
  const base = is_https === true ? return_origin + '/sso/callback' : return_origin;
  const sep = base.indexOf('?') === -1 ? '?' : '&';
  const qs = new URLSearchParams(params).toString();
  return base + sep + qs;
}

const primary_button_style: React.CSSProperties = {
  background: '#BCCF17',
  color: '#1a1a1a',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
};
